import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Truck, MapPin, Phone, User, Mail, Loader2, ChevronRight, ShieldCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const font = { fontFamily: "'Outfit', sans-serif" };

const SA_PROVINCES = [
  "Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo",
  "Mpumalanga", "North West", "Northern Cape", "Western Cape",
];

/* --- Sub-components --- */

const ProductBanner = ({ product, price }) => (
  <>
    <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
      <Package size={20} style={{ color: "var(--ll-green)" }} />
      <div className="flex-1">
        <p className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>{product.name}</p>
        <p className="text-xs" style={{ color: "var(--ll-text-muted)", ...font }}>Physical workbook · Free shipping</p>
      </div>
      <div className="text-right">
        <p className="font-serif text-lg font-semibold" style={{ color: "var(--ll-text)" }}>R{price}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "rgba(163,184,153,0.15)" }}>
      <Truck size={14} style={{ color: "var(--ll-green)" }} />
      <p className="text-xs" style={{ color: "var(--ll-green)", ...font }}>Free shipping · Estimated delivery ~2 weeks · South Africa only</p>
    </div>
  </>
);

const DeliveryForm = ({ form, updateField, sameAsBuyer, onSameAsBuyerChange, onProceed }) => (
  <div className="space-y-4 pt-1">
    <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--ll-text-muted)", ...font }}>Your Details</p>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>Your Name *</Label>
        <Input data-testid="phys-customer-name" value={form.customer_name}
          onChange={(e) => { updateField("customer_name", e.target.value); if (sameAsBuyer) updateField("recipient_name", e.target.value); }}
          placeholder="Your full name" className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>Your Email *</Label>
        <Input data-testid="phys-customer-email" type="email" value={form.customer_email} onChange={(e) => updateField("customer_email", e.target.value)}
          placeholder="you@example.com" className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
      </div>
    </div>

    <div className="flex items-center gap-2 pt-1">
      <div className="border-t flex-1" style={{ borderColor: "var(--ll-border)" }} />
      <p className="text-[10px] tracking-[0.2em] uppercase flex-shrink-0" style={{ color: "var(--ll-text-muted)", ...font }}>Delivery To</p>
      <div className="border-t flex-1" style={{ borderColor: "var(--ll-border)" }} />
    </div>

    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={sameAsBuyer} onChange={(e) => onSameAsBuyerChange(e.target.checked)} className="rounded" />
      <span className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>Deliver to myself</span>
    </label>

    {!sameAsBuyer && (
      <div className="space-y-1.5">
        <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>Recipient Name *</Label>
        <Input data-testid="phys-recipient-name" value={form.recipient_name} onChange={(e) => updateField("recipient_name", e.target.value)}
          placeholder="Recipient full name" className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
      </div>
    )}

    <div className="space-y-1.5">
      <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}><MapPin size={11} className="inline mr-1" />Delivery Address *</Label>
      <Input data-testid="phys-delivery-address" value={form.delivery_address} onChange={(e) => updateField("delivery_address", e.target.value)}
        placeholder="Street address" className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
    </div>

    <div className="grid grid-cols-3 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>City</Label>
        <Input data-testid="phys-city" value={form.city} onChange={(e) => updateField("city", e.target.value)}
          placeholder="City" className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>Province</Label>
        <select data-testid="phys-province" value={form.province} onChange={(e) => updateField("province", e.target.value)}
          className="w-full rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "var(--ll-border)", background: "white" }}>
          <option value="">Select</option>
          {SA_PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>Postal Code</Label>
        <Input data-testid="phys-postal-code" value={form.postal_code} onChange={(e) => updateField("postal_code", e.target.value)}
          placeholder="0001" className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
      </div>
    </div>

    <div className="space-y-1.5">
      <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>Or PUDO Locker Location (alternative)</Label>
      <Input data-testid="phys-pudo" value={form.pudo_location} onChange={(e) => updateField("pudo_location", e.target.value)}
        placeholder="e.g., PUDO Locker - Mall of Africa" className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
    </div>

    <div className="space-y-1.5">
      <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}><Phone size={11} className="inline mr-1" />Recipient Mobile * <span className="text-[10px]" style={{ color: "var(--ll-text-muted)" }}>(for courier notifications)</span></Label>
      <Input data-testid="phys-mobile" type="tel" value={form.recipient_mobile} onChange={(e) => updateField("recipient_mobile", e.target.value)}
        placeholder="072 123 4567" className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
    </div>

    <div className="space-y-1.5">
      <Label className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>Delivery Notes (optional)</Label>
      <Input data-testid="phys-notes" value={form.delivery_notes} onChange={(e) => updateField("delivery_notes", e.target.value)}
        placeholder="Gate code, building name, etc." className="rounded-xl border text-sm" style={{ borderColor: "var(--ll-border)" }} />
    </div>

    <button data-testid="phys-proceed-btn" onClick={onProceed}
      className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
      style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", ...font }}>
      Review Order <ChevronRight size={16} />
    </button>
  </div>
);

const ReviewStep = ({ form, product, price, loading, onBack, onPay }) => (
  <div className="space-y-4 pt-1">
    <div className="rounded-xl p-4 space-y-2" style={{ background: "var(--ll-bg-secondary)" }}>
      <p className="text-[10px] tracking-[0.15em] uppercase mb-1" style={{ color: "var(--ll-text-muted)", ...font }}>Delivery Summary</p>
      <div className="flex items-start gap-2">
        <User size={13} style={{ color: "var(--ll-text-muted)", marginTop: 2 }} />
        <div>
          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>{form.recipient_name}</p>
          <p className="text-xs" style={{ color: "var(--ll-text-muted)", ...font }}>{form.recipient_mobile}</p>
        </div>
      </div>
      <div className="flex items-start gap-2">
        <MapPin size={13} style={{ color: "var(--ll-text-muted)", marginTop: 2 }} />
        <p className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>
          {form.pudo_location || [form.delivery_address, form.city, form.province, form.postal_code].filter(Boolean).join(", ")}
        </p>
      </div>
      <div className="flex items-start gap-2">
        <Mail size={13} style={{ color: "var(--ll-text-muted)", marginTop: 2 }} />
        <p className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>{form.customer_email}</p>
      </div>
      {form.delivery_notes && (
        <div className="flex items-start gap-2">
          <FileText size={13} style={{ color: "var(--ll-text-muted)", marginTop: 2 }} />
          <p className="text-xs" style={{ color: "var(--ll-text-secondary)", ...font }}>{form.delivery_notes}</p>
        </div>
      )}
    </div>

    <div className="rounded-xl p-4" style={{ background: "var(--ll-bg-secondary)" }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm" style={{ color: "var(--ll-text-secondary)", ...font }}>{product.name}</span>
        <span className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>R{price}</span>
      </div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm" style={{ color: "var(--ll-text-secondary)", ...font }}>Shipping</span>
        <span className="text-sm font-medium" style={{ color: "var(--ll-green)", ...font }}>Free</span>
      </div>
      <div className="border-t pt-2 mt-2 flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
        <span className="text-sm font-medium" style={{ color: "var(--ll-text)", ...font }}>Total</span>
        <span className="font-serif text-xl font-semibold" style={{ color: "var(--ll-text)" }}>R{price}</span>
      </div>
    </div>

    <div className="flex gap-2">
      <button onClick={onBack} className="rounded-full px-5 py-3 text-sm font-medium border transition-all hover:-translate-y-0.5"
        style={{ borderColor: "var(--ll-border)", color: "var(--ll-text-secondary)", ...font }}>Back</button>
      <button data-testid="phys-pay-btn" onClick={onPay} disabled={loading}
        className="flex-1 rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", ...font }}>
        {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
        {loading ? "Processing..." : `Pay R${price} via PayFast`}
      </button>
    </div>

    <p className="text-[10px] text-center" style={{ color: "var(--ll-text-muted)", ...font }}>
      Secure payment via PayFast. Use a different email than your PayFast merchant account.
    </p>
  </div>
);

/* --- Main Modal --- */

const INITIAL_FORM = {
  customer_name: "", customer_email: "",
  recipient_name: "", delivery_address: "", city: "", province: "", postal_code: "",
  pudo_location: "", recipient_mobile: "", delivery_notes: "",
};

const PhysicalCheckoutModal = ({ open, onClose, product }) => {
  const [step, setStep] = useState("details");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [sameAsBuyer, setSameAsBuyer] = useState(true);

  if (!product) return null;
  const price = product.launch_price_zar || product.price_zar;

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSameAsBuyer = (checked) => {
    setSameAsBuyer(checked);
    if (checked) updateField("recipient_name", form.customer_name);
  };

  const handleProceed = () => {
    if (!form.customer_name.trim()) { toast.error("Please enter your name."); return; }
    if (!form.customer_email.trim() || !form.customer_email.includes("@")) { toast.error("Please enter a valid email."); return; }
    if (!form.recipient_name.trim()) { toast.error("Please enter the recipient name."); return; }
    if (!form.recipient_mobile.trim()) { toast.error("Mobile number is required for courier notifications."); return; }
    if (!form.delivery_address.trim() && !form.pudo_location.trim()) { toast.error("Please enter a delivery address or PUDO locker location."); return; }
    setStep("review");
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/physical`, { product_id: product.id, ...form });
      const { payfast_url, form_data } = res.data;
      const formEl = document.createElement("form");
      formEl.method = "POST";
      formEl.action = payfast_url;
      formEl.target = "_blank";
      Object.entries(form_data).forEach(([key, val]) => {
        const input = document.createElement("input");
        input.type = "hidden"; input.name = key; input.value = val;
        formEl.appendChild(input);
      });
      document.body.appendChild(formEl);
      formEl.submit();
      document.body.removeChild(formEl);
      toast.success("PayFast payment opened in a new tab.");
      handleClose();
    } catch {
      toast.error("Could not process checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setStep("details"); setForm(INITIAL_FORM); setSameAsBuyer(true); onClose(); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="physical-checkout-modal" className="sm:max-w-lg rounded-2xl border max-h-[90vh] overflow-y-auto"
        style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg)" }}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium" style={{ color: "var(--ll-text)" }}>
            {step === "details" ? "Delivery Details" : "Review & Pay"}
          </DialogTitle>
          <DialogDescription className="sr-only">Order {product.name}</DialogDescription>
        </DialogHeader>
        <ProductBanner product={product} price={price} />
        {step === "details" && <DeliveryForm form={form} updateField={updateField} sameAsBuyer={sameAsBuyer} onSameAsBuyerChange={handleSameAsBuyer} onProceed={handleProceed} />}
        {step === "review" && <ReviewStep form={form} product={product} price={price} loading={loading} onBack={() => setStep("details")} onPay={handlePayment} />}
      </DialogContent>
    </Dialog>
  );
};

export default PhysicalCheckoutModal;
